import {Modal, ModalBody, ModalFooter, Input, ButtonGroup} from "reactstrap";
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import SimpleBar from "simplebar-react";
import CreatableSelect from 'react-select/creatable';
import { useTranslation } from 'react-i18next';
import { createSelector } from "reselect";
import {typeKaraoke} from "../constants/default";
import { useSelector, useDispatch } from "react-redux";
import {eventSongAddReset} from "../../slices/event/song/add/reducer";
import {viewEventSongs} from "../../slices/event/view/thunk";
import {addSongAdmin, addSongGuest} from "../../slices/event/song/add/thunk";
import {viewEventUsers} from "../../slices/event/user/list/thunk";
import {viewSongs} from "../../slices/song/thunk";
import {authUserManager} from "../../helpers/api_helper";
import {notificationService} from "../../helpers/NotificationService";
import {normalizeSongText, songQueryTokens, matchesSongQuery, songMatchRank} from "../../helpers/songSearch";

// Maksymalnie tyle wyników renderujemy naraz (przy pełnej bazie lista bywa ogromna).
const MAX_RESULTS = 50;

// Dolna granica wysokości listy — przy bardzo niskim oknie (np. klawiatura na mobile)
// lista nadal ma pokazać kilka pozycji zamiast zwinąć się do zera.
const MIN_LIST_HEIGHT = 160;

export const AddEventSongModal = ({event, isAddSongModalOpen, togModalAddSong, eventStartAt, isEventAdmin, eventType, eventId, isClubAdmin, guestId, requireTip, onPay}) => {
  const { t } = useTranslation();
  const isDarkMode = useSelector(state => state.Layout.layoutModeType) === 'dark';
  const dispatch = useDispatch();
  const isKaraoke = eventType === typeKaraoke;
  const loadFullDatabase = !!event?.loadFullSongDatabase;
  const canAddFromOutside = event?.allowGuestsAddSongsFromDatabaseOutside !== false || isEventAdmin;

  // SONG LIST
  const selectSongList = createSelector(
    (state) => state.SongList,
    (slice) => ({ songList: slice.list })
  );
  const { songList } = useSelector(selectSongList);

  // EVENT USERS LIST (tylko admin)
  const selectEventUsersList = createSelector(
    (state) => state.EventUsersList,
    (slice) => ({ eventUsersList: slice.list })
  );
  const { eventUsersList } = useSelector(selectEventUsersList);

  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  // Rozwinięty wiersz (jednocześnie tylko jeden): { key, mode: 'add' | 'note' }.
  // 'add' - sam wybór użytkownika (club admin), 'note' - dodatkowo pole dedykacji.
  const [expanded, setExpanded] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteError, setNoteError] = useState(null);
  // Wiersz, do którego przypisana jest aktualna dedykacja (zostaje po zwinięciu koperty).
  const [noteRowKey, setNoteRowKey] = useState(null);
  // Wskaźnik "przewiń dalej" gdy nie widać wszystkich wyników.
  const [showBottomArrow, setShowBottomArrow] = useState(false);
  // Wysokość listy wyników liczona do dołu ekranu (patrz computeListMaxHeight).
  const [listMaxHeight, setListMaxHeight] = useState("50vh");
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (isAddSongModalOpen) {
      dispatch(viewSongs(event?.id || null));
      if (isEventAdmin) dispatch(viewEventUsers(eventId));
      // Klub-admin dodaje domyślnie w swoim imieniu.
      if (isClubAdmin) {
        // Username jest opcjonalny — bez niego pokazujemy email admina.
        const authUser = authUserManager.getAuthUser();
        setSelectedUser({ value: authUserManager.getAdminId(), label: authUser.username || authUser.email });
      }
    } else {
      // Reset stanu po zamknięciu.
      dispatch(eventSongAddReset());
      setSearch("");
      setSelectedUser(null);
      setExpanded(null);
      setNoteText("");
      setNoteError(null);
      setNoteRowKey(null);
    }
  }, [isAddSongModalOpen]);

  // ADD EVENT SONG - wynik
  const selectAddEventSong = createSelector(
    (state) => state.EventSongAdd,
    (slice) => ({
      addEventSongSuccess: slice.success,
      addEventSongError: slice.violations,
    })
  );
  const { addEventSongError, addEventSongSuccess } = useSelector(selectAddEventSong);

  useEffect(() => {
    if (addEventSongError?.length > 0) {
      if (Array.isArray(addEventSongError)) {
        addEventSongError.forEach((violation) => notificationService.error(t(violation.message)));
      } else {
        notificationService.error(t(addEventSongError));
      }
      dispatch(eventSongAddReset());
    }
  }, [addEventSongError]);

  useEffect(() => {
    if (addEventSongSuccess) {
      if (isAddSongModalOpen) togModalAddSong();
      dispatch(viewEventSongs(eventId));
      dispatch(eventSongAddReset());
    }
  }, [addEventSongSuccess]);

  // Znormalizowana baza liczona raz na listę, a nie przy każdym naciśnięciu klawisza
  // (przy pełnej bazie to tysiące pozycji).
  const normalizedSongList = useMemo(
    () => songList.map((s) => ({ song: s, normalized: normalizeSongText(s.song) })),
    [songList]
  );

  // Dopasowane utwory (sortowanie identyczne jak w wyszukiwarce listy).
  const filteredSongs = useMemo(() => {
    if (!search || search.trim().length === 0) {
      // Przy pełnej bazie i pustym polu pokazujemy całą listę do przewijania.
      return loadFullDatabase ? songList.slice(0, MAX_RESULTS) : [];
    }
    const nv = normalizeSongText(search);
    const tokens = songQueryTokens(search);

    return normalizedSongList
      .filter((entry) => matchesSongQuery(entry.normalized, tokens))
      .sort((a, b) => songMatchRank(a.normalized, nv) - songMatchRank(b.normalized, nv))
      .slice(0, MAX_RESULTS)
      .map((entry) => entry.song);
  }, [search, songList, normalizedSongList, loadFullDatabase]);

  // Czy wpisany tekst jest już dokładnie w bazie (żeby nie proponować dodania spoza bazy).
  const hasExactMatch = useMemo(() => {
    if (!search.trim()) return false;
    const nv = normalizeSongText(search);
    return normalizedSongList.some((entry) => entry.normalized === nv);
  }, [search, normalizedSongList]);

  const showOutsideRow = canAddFromOutside && search.trim().length > 0 && !hasExactMatch;
  // Nagłówek "Wyszukane utwory" ma sens tylko przy aktywnym wyszukiwaniu
  // (przy przeglądaniu całej bazy bez frazy lista nie jest wynikiem wyszukiwania).
  const showSearchedHeader = search.trim().length > 0;
  const showNotFound = !canAddFromOutside && search.trim().length > 0 && filteredSongs.length === 0;

  // Zbuduj payload i wykonaj dodanie (od razu, bez kroku "Zapisz").
  const doAdd = (songPart, note) => {
    if (isEventAdmin && !selectedUser) {
      notificationService.error(t("This field can't be empty"));
      return;
    }
    const payload = { eventId, song: songPart };
    if (note && note.trim().length >= 2) payload.guestNote = note.trim();

    if (isEventAdmin) {
      payload.user = selectedUser.__isNew__
        ? { id: null, username: selectedUser.value }
        : { id: selectedUser.value };
      dispatch(addSongAdmin(payload));
    } else {
      payload.user = { id: guestId, username: null };
      if (requireTip) {
        // Wymagany napiwek: przekaż wybór do bramki płatności zamiast dodawać od razu.
        onPay(payload);
      } else {
        dispatch(addSongGuest(payload));
      }
    }
  };

  // Rozwiń/zwiń wiersz w danym trybie. Ten sam przycisk na otwartym wierszu zwija.
  // Dedykacja przypisana do wiersza zostaje po zwinięciu; czyścimy ją tylko przy zmianie wiersza.
  const toggleExpand = (key, mode) => {
    setNoteError(null);
    const isClosing = expanded && expanded.key === key && expanded.mode === mode;
    setExpanded(isClosing ? null : { key, mode });
    if (mode === 'note' && noteRowKey !== key) {
      setNoteText("");
      setNoteRowKey(key);
    }
  };

  // Kliknięcie plusa: club admin rozwija wybór użytkownika.
  // Gość: dodaje utwór z dedykacją, jeśli została wpisana dla tego wiersza (nawet gdy zwinięta).
  const handleAddClick = (key, songPart) => {
    if (isEventAdmin) {
      toggleExpand(key, 'add');
      return;
    }
    confirmAdd(songPart, key);
  };

  const confirmAdd = (songPart, key) => {
    const note = noteRowKey === key ? noteText.trim() : "";
    if (note.length === 1) {
      setNoteError(t("Note must be at least 2 characters long"));
      return;
    }
    doAdd(songPart, note || null);
  };

  // Lista ma sięgać do dołu ekranu, gdy wyników jest dużo (a przy krótkiej liście
  // modal zostaje niski). Liczymy dostępne miejsce od górnej krawędzi listy do dołu
  // okna, odejmując stopkę modala, dolny padding body i margines dialogu — dzięki
  // temu działa tak samo na desktopie i mobile (gdzie marginesy są mniejsze).
  const computeListMaxHeight = useCallback(() => {
    const sb = scrollContainerRef.current;
    const el = sb?.getScrollElement ? sb.getScrollElement() : sb;
    if (!el) return;

    const body = el.closest('.modal-body');
    const dialog = el.closest('.modal-dialog');
    const footer = el.closest('.modal-content')?.querySelector('.modal-footer');

    const bodyPaddingBottom = body ? parseFloat(getComputedStyle(body).paddingBottom) : 16;
    const dialogMarginBottom = dialog ? parseFloat(getComputedStyle(dialog).marginBottom) : 16;
    // visualViewport: na mobile klawiatura zasłania część ekranu, window.innerHeight tego nie widzi.
    const vv = window.visualViewport;
    const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const available = viewportBottom - el.getBoundingClientRect().top
      - bodyPaddingBottom - dialogMarginBottom - (footer?.offsetHeight ?? 0);

    setListMaxHeight(`${Math.max(MIN_LIST_HEIGHT, Math.round(available))}px`);
  }, []);

  useEffect(() => {
    if (!isAddSongModalOpen) return;
    window.addEventListener('resize', computeListMaxHeight);
    window.visualViewport?.addEventListener('resize', computeListMaxHeight);
    window.visualViewport?.addEventListener('scroll', computeListMaxHeight);
    return () => {
      window.removeEventListener('resize', computeListMaxHeight);
      window.visualViewport?.removeEventListener('resize', computeListMaxHeight);
      window.visualViewport?.removeEventListener('scroll', computeListMaxHeight);
    };
  }, [isAddSongModalOpen, computeListMaxHeight]);

  // Pokaż strzałkę, jeśli w liście jest więcej treści poniżej widocznego obszaru.
  const checkScroll = useCallback(() => {
    const sb = scrollContainerRef.current;
    const el = sb?.getScrollElement ? sb.getScrollElement() : sb;
    if (!el) return;
    const hasMore = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    setShowBottomArrow(hasMore && !atBottom);
  }, []);

  useEffect(() => {
    const sb = scrollContainerRef.current;
    const el = sb?.getScrollElement ? sb.getScrollElement() : sb;
    if (!el) return;
    const id = setTimeout(checkScroll, 100);
    el.addEventListener('scroll', checkScroll);
    return () => {
      clearTimeout(id);
      el.removeEventListener('scroll', checkScroll);
    };
  }, [checkScroll, filteredSongs, showOutsideRow, isAddSongModalOpen, expanded, listMaxHeight]);

  // Tytuł/wykonawca osobno. Backend zwraca je wprost; fallback rozbija "Wykonawca - Tytuł".
  const songTitle = (s) => s.title ?? (s.song?.includes(' - ') ? s.song.split(' - ').slice(1).join(' - ') : s.song);
  const songArtist = (s) => s.artist ?? (s.song?.includes(' - ') ? s.song.split(' - ')[0] : '');

  // Nagłówek grupy w liście wyników (np. "Dodaj nowy" / "Wyszukane utwory").
  const renderGroupHeader = (label) => (
    <div
      className="text-muted fw-semibold text-uppercase text-center lh-1"
      style={{ fontSize: '0.8rem', letterSpacing: '0.04em'}}
    >
      {label}
    </div>
  );

  const renderRow = (key, title, artist, songPart) => {
    const isOpen = expanded?.key === key;
    const isNoteOpen = isOpen && expanded.mode === 'note';
    const isAddOpen = isOpen && expanded.mode === 'add';
    return (
      <div key={key}>
        <div className="event-song-row song ps-2 p-1">
          <div className="event-song-text">
            <div className="text-container-mobile fw-medium">{title}</div>
            {/* Rozmiar wykonawcy w custom.scss — na telefonie jest odrobinę większy. */}
            {artist && <div className="text-container-mobile event-song-artist text-muted mb-0">{artist}</div>}
          </div>
          <ButtonGroup className="gap-1 flex-shrink-0 ms-2">
            {!isEventAdmin && (
              <button
                type="button"
                className={`btn btn-sm btn-icon ${isNoteOpen ? 'text-success' : 'text-white'}`}
                onClick={() => toggleExpand(key, 'note')}
                title={isKaraoke ? t('Additional Info for the Host') : t('Add with dedication')}
              >
                <i className="ri-mail-add-line fs-20"></i>
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm btn-icon ${isAddOpen ? 'text-success' : 'text-white'}`}
              onClick={() => handleAddClick(key, songPart)}
              title={t('Add')}
            >
              <i className="ri-add-circle-line fs-20"></i>
            </button>
          </ButtonGroup>
        </div>
        {isOpen && (
          <div className="px-2 pb-2">
            {isEventAdmin && (
              <div className="d-flex gap-2 align-items-center mb-2">
                <div className="flex-grow-1">
                  <CreatableSelect
                    darkMode={isDarkMode}
                    styles={{
                      input: (base, state) => ({ ...base, color: state.selectProps.darkMode ? '#fff' : '#000' }),
                    }}
                    className="form-control-no-padding form-control-lg form-control-large-high"
                    isClearable
                    formatCreateLabel={(inputValue) => `${t('Click to Add')} "${inputValue}"`}
                    onChange={(newValue) => setSelectedUser(newValue)}
                    options={eventUsersList}
                    value={selectedUser}
                    placeholder={t('Select or add user')}
                  />
                </div>
                <button
                  type="button"
                  className="btn bg-transparent border-0 shadow-none color-primary btn-icon flex-shrink-0"
                  onClick={() => confirmAdd(songPart, key)}
                  title={t('Add')}
                >
                  <i className="ri-check-line fs-20"></i>
                </button>
              </div>
            )}
            {isNoteOpen && (
              <div className="d-flex gap-2 align-items-start">
                <div className="flex-grow-1">
                  <Input
                    type="text"
                    maxLength={150}
                    autoFocus
                    className={noteError ? 'is-invalid' : ''}
                    placeholder={isKaraoke ? t("Note for the Host") : t('Enter dedication')}
                    value={noteText}
                    onChange={(e) => { setNoteText(e.target.value); setNoteError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmAdd(songPart, key); } }}
                  />
                  {noteError && <div className="invalid-feedback d-block">{noteError}</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    // onOpened, a nie efekt po renderze: w trakcie animacji otwarcia dialog jest
    // przesunięty transformem, więc pozycja listy byłaby policzona za wysoko.
    <Modal isOpen={isAddSongModalOpen} toggle={togModalAddSong} onOpened={computeListMaxHeight}>
      <ModalBody>
        <div className="app-search p-0 mb-2">
          <div className="position-relative">
            <Input
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              type="text"
              className="form-control form-control-lg"
              placeholder={t("Search and add a song")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ backgroundImage: 'none' }}
            />
            <span className="mdi mdi-magnify search-widget-icon"></span>
            {search.length > 0 && (
              <span
                className="mdi mdi-close-circle search-widget-icon search-widget-icon-close"
                onClick={() => setSearch("")}
                style={{ cursor: "pointer" }}
              ></span>
            )}
          </div>
        </div>

        <div className="position-relative">
          <SimpleBar style={{ maxHeight: listMaxHeight }} ref={scrollContainerRef}>
            <div className="event-song-list event-song-list-search list">
              {showOutsideRow && (
                <div>
                  {renderGroupHeader(t('Add new'))}
                  {renderRow('outside', search.trim(), null, { id: null, artistTitle: search.trim() })}
                </div>
              )}

              {filteredSongs.length > 0 && (
                // Wyniki z bazy jako osobna grupa — oddzielona od wiersza "Dodaj nowy".

                <div className={showOutsideRow ? 'border-top pt-2 mt-1' : ''}>
                  {showSearchedHeader && renderGroupHeader(t('Searched songs'))}
                  {filteredSongs.map((s) => renderRow(`db-${s.id}`, songTitle(s), songArtist(s), { id: s.id }))}
                </div>
              )}

              {showNotFound && (
                <div className="text-center text-muted py-4">
                  <i className="ri-search-line d-block mb-2" style={{ fontSize: '2rem' }}></i>
                  {t('Not found song')}
                </div>
              )}

              {!search.trim() && !loadFullDatabase && filteredSongs.length === 0 && (
                <div className="text-center text-muted py-4">
                  <i className="ri-music-2-line d-block mb-2" style={{ fontSize: '2rem' }}></i>
                  {t('Search For a Song')}
                </div>
              )}
            </div>
          </SimpleBar>

          {showBottomArrow && (
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "50%",
                transform: "translateX(-50%)",
                pointerEvents: "none",
                zIndex: 10,
              }}
            >
              <span className="scroll-more-arrow" aria-hidden="true">
                <i className="bx bx-chevrons-down"></i>
              </span>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="border-top-0 pt-0">
        <button
          type="button"
          className="btn bg-transparent border-0 shadow-none color-primary"
          onClick={togModalAddSong}
        >
          {t('Close')}
        </button>
      </ModalFooter>
    </Modal>
  );
}
